import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import ukLocale from '@angular/common/locales/uk';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, isDevMode } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { AuthEffects, AuthInterceptor, AuthService, authReducer } from '@lib/auth';
import { medicalDocumentsReducer } from '@lib/medical-documents';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { initializeApp } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';
import { getRemoteConfig } from 'firebase/remote-config';

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { hmReducer } from '@hm/core/stores/hm/hm.reducer';
import { memberHubReducer } from '@hm/core/stores/member-hub/member-hub.reducer';
import { prescriptionsReducer } from '@hm/core/stores/prescriptions/prescriptions.reducer';

import { environment } from '@hm/environments/environment';

import { FeatureFlagService } from '@lib/core/services/feature-flag.service';
import { provideTranslateModule } from '@lib/core/translate/translate.provider';

import { MD_API_URL } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import { API_URL, AUTH_API_URL, PROTECTED_API_URL } from '../../../../packages/core/src/tokens/api.tokens';
import { FIREBASE_API_URL, FIREBASE_CONFIG, FIREBASE_MESSAGING } from '../../../../packages/core/src/tokens/firebase.tokens';
import * as medicalDocumentsEffects from '../../../../packages/medical-documents/src/core/store/medical-documents.effects';
import { provideChatLibConfig } from '../core/providers/provide-chat-config.function';
import * as hmEffects from './../core/stores/hm/hm.effects';
import * as memberHubEffects from './../core/stores/member-hub/member-hub.effects';
import * as prescriptionsEffects from './../core/stores/prescriptions/prescriptions.effects';
import { routes } from './app.routes';

registerLocaleData(ukLocale);

export const firebaseApp = initializeApp(environment.firebase);
export const messaging = getMessaging(firebaseApp);
const remoteConfig = getRemoteConfig(firebaseApp);
remoteConfig.settings = {
  fetchTimeoutMillis: 0,
  minimumFetchIntervalMillis: 0,
};

function initializeTokens(authService: AuthService) {
  return () => authService.initializeTokens();
}

export function initializeFeatureFlags(featureFlagService: FeatureFlagService) {
  return () => featureFlagService.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideStore({
      auth: authReducer,
      hm: hmReducer,
      prescriptions: prescriptionsReducer,
      memberHub: memberHubReducer,
      medicalDocuments: medicalDocumentsReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([AuthEffects, hmEffects, prescriptionsEffects, memberHubEffects, medicalDocumentsEffects]),
    provideHttpClient(withInterceptors([AuthInterceptor])),
    provideTranslateModule(),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTokens,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      deps: [FeatureFlagService],
      multi: true,
    },
    { provide: AUTH_API_URL, useValue: environment.authorizationApiUrl },
    { provide: API_URL, useValue: environment.healthManagerApiUrl },
    { provide: PROTECTED_API_URL, useValue: environment.protectedApiUrl },

    { provide: FIREBASE_API_URL, useValue: environment.healthManagerApiUrl },
    { provide: FIREBASE_CONFIG, useValue: environment.firebase },
    { provide: FIREBASE_MESSAGING, useValue: messaging },
    { provide: LOCALE_ID, useValue: 'uk' },
    { provide: MD_API_URL, useValue: environment.protectedApiUrl },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideNativeDateAdapter(),
    provideChatLibConfig(),
  ],
};
